import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ShieldCheck, Loader2, CheckCircle2, AlertCircle, Clock,
  ChevronLeft, ChevronRight, Camera, IdCard, ScanFace, RefreshCw,
} from 'lucide-react';
import { useIdentityVerification } from '../../hooks/useAPI';
import CameraCapture, { type CameraShape } from './CameraCapture';

interface IdentityVerificationScreenProps {
  onDone: () => void;
  onSkip: () => void;
  variant?: 'onboarding' | 'standalone';
}

type SlotKey = 'idFront' | 'idBack' | 'selfie';

const CAPTURE_STEPS: Array<{
  key: SlotKey;
  title: string;
  subtitle: string;
  hint: string;
  icon: typeof IdCard;
  facing: 'user' | 'environment';
  shape: CameraShape;
}> = [
  {
    key: 'idFront',
    title: 'Emirates ID — Front',
    subtitle: 'Step 1 of 3',
    hint: 'Lay the card flat, fill the frame, all 4 corners visible.',
    icon: IdCard,
    facing: 'environment',
    shape: 'card',
  },
  {
    key: 'idBack',
    title: 'Emirates ID — Back',
    subtitle: 'Step 2 of 3',
    hint: 'Flip the card over. Avoid glare on the chip.',
    icon: IdCard,
    facing: 'environment',
    shape: 'card',
  },
  {
    key: 'selfie',
    title: 'Take a selfie',
    subtitle: 'Step 3 of 3',
    hint: 'Look straight at the camera in good lighting. No sunglasses or hats.',
    icon: ScanFace,
    facing: 'user',
    shape: 'face',
  },
];

type Phase = 'intro' | 'capture' | 'review' | 'submitting' | 'done' | 'pending' | 'error';

export default function IdentityVerificationScreen({
  onDone,
  onSkip,
  variant = 'onboarding',
}: IdentityVerificationScreenProps) {
  const { submitVerification, isLoading } = useIdentityVerification();

  const [phase, setPhase] = useState<Phase>('intro');
  const [stepIndex, setStepIndex] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [files, setFiles] = useState<Partial<Record<SlotKey, File>>>({});
  const [previews, setPreviews] = useState<Partial<Record<SlotKey, string>>>({});
  const [message, setMessage] = useState<string | null>(null);

  const step = CAPTURE_STEPS[stepIndex];
  const currentFile = files[step?.key];
  const currentPreview = previews[step?.key];
  const progress = ((stepIndex + 1) / CAPTURE_STEPS.length) * 100;

  const setSlot = (key: SlotKey, file: File, previewUrl?: string) => {
    if (file.size > 8 * 1024 * 1024) {
      setMessage('File is too large — max 8 MB per image.');
      return;
    }
    setMessage(null);
    setFiles((p) => ({ ...p, [key]: file }));
    setPreviews((p) => {
      if (p[key]) URL.revokeObjectURL(p[key]!);
      return { ...p, [key]: previewUrl ?? URL.createObjectURL(file) };
    });
  };

  const onCameraCapture = (file: File, previewUrl: string) => {
    setSlot(step.key, file, previewUrl);
    setCameraOpen(false);
  };

  const goNext = () => {
    if (stepIndex < CAPTURE_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      setPhase('review');
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    } else {
      setPhase('intro');
    }
  };

  const handleSubmit = async () => {
    setPhase('submitting');
    setMessage(null);
    const result = await submitVerification({
      idFront: files.idFront!,
      idBack: files.idBack!,
      selfie: files.selfie!,
    });
    if (result.status === 'verified') {
      setPhase('done');
      setMessage('Verified — AED 500 deposit waived on all bookings.');
      setTimeout(onDone, 1800);
    } else if (result.status === 'pending_review') {
      setPhase('pending');
      setMessage("Submitted. Our team will confirm within a few hours — you'll get a notification.");
    } else {
      setPhase('error');
      setMessage(result.error?.message || 'Something went wrong. Please try again.');
    }
  };

  // --- Rendering -----------------------------------------------------------

  const header = (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={onSkip}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          aria-label="Close"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg">Identity Verification</h1>
          <p className="text-xs text-muted-foreground">
            {phase === 'capture' ? step?.subtitle : 'Waive the AED 500 deposit'}
          </p>
        </div>
      </div>
      {phase === 'capture' && (
        <div className="h-1 bg-secondary">
          <div
            className="h-1 bg-[#1A6B4F] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );

  const transition = { duration: 0.25, ease: 'easeOut' as const };

  return (
    <div className="w-full min-h-screen bg-background flex flex-col">
      {header}

      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          {/* -------- INTRO -------- */}
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={transition}
              className="absolute inset-0 flex flex-col px-6 py-8"
            >
              <div className="flex-1 flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full bg-[#D4F04A]/20 flex items-center justify-center mb-6">
                  <ShieldCheck className="w-12 h-12 text-[#1A6B4F]" />
                </div>
                <h2 className="text-2xl mb-3">Verify once, save on every rental</h2>
                <p className="text-muted-foreground max-w-sm mb-8">
                  Our AI checks your Emirates ID and selfie in seconds. Once verified,
                  the <span className="font-semibold text-foreground">AED 500</span>{' '}
                  security deposit is waived on all future bookings.
                </p>
                <div className="w-full max-w-sm space-y-3 text-left mb-8">
                  <StepHint num={1} label="Front of Emirates ID" />
                  <StepHint num={2} label="Back of Emirates ID" />
                  <StepHint num={3} label="Selfie" />
                </div>
                <p className="text-xs text-muted-foreground max-w-sm">
                  Documents are encrypted and used only for verification.
                </p>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => { setStepIndex(0); setPhase('capture'); }}
                  className="w-full h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white flex items-center justify-center gap-2"
                >
                  Get started
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={onSkip}
                  className="w-full h-10 text-sm text-muted-foreground hover:text-foreground"
                >
                  {variant === 'onboarding' ? 'Skip for now' : 'Close'}
                </button>
              </div>
            </motion.div>
          )}

          {/* -------- CAPTURE -------- */}
          {phase === 'capture' && step && (
            <motion.div
              key={`capture-${step.key}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={transition}
              className="absolute inset-0 flex flex-col px-6 py-6"
            >
              <div className="flex-1">
                <div className="w-14 h-14 rounded-2xl bg-[#D4F04A]/20 flex items-center justify-center mb-4">
                  <step.icon className="w-7 h-7 text-[#1A6B4F]" />
                </div>
                <h2 className="text-2xl mb-2">{step.title}</h2>
                <p className="text-muted-foreground mb-6">{step.hint}</p>

                <button
                  type="button"
                  onClick={() => setCameraOpen(true)}
                  className={`w-full rounded-2xl border-2 border-dashed overflow-hidden transition-colors ${
                    currentPreview
                      ? 'border-[#1A6B4F] bg-[#D4F04A]/10'
                      : 'border-border bg-card hover:border-[#1A6B4F]/40'
                  }`}
                >
                  {currentPreview ? (
                    <div className="relative">
                      <img src={currentPreview} alt="" className="w-full h-64 object-cover" />
                      <div className="absolute top-2 right-2 bg-[#1A6B4F] text-white text-xs rounded-full px-3 py-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Captured
                      </div>
                    </div>
                  ) : (
                    <div className="h-64 flex flex-col items-center justify-center gap-3 p-6 bg-gradient-to-br from-[#1A6B4F]/5 to-[#D4F04A]/5">
                      <div className="w-14 h-14 rounded-full bg-[#1A6B4F] flex items-center justify-center">
                        <Camera className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-sm font-medium">Open camera</div>
                      <div className="text-xs text-muted-foreground">
                        {step.shape === 'card'
                          ? 'Align the card inside the frame'
                          : 'Centre your face in the oval'}
                      </div>
                    </div>
                  )}
                </button>

                {currentPreview && (
                  <button
                    onClick={() => setCameraOpen(true)}
                    className="mt-3 w-full h-10 text-sm text-[#1A6B4F] hover:bg-secondary rounded-xl flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retake
                  </button>
                )}

                {message && (
                  <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-xl p-3 flex gap-2 text-destructive">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{message}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={goBack}
                  className="h-14 px-6 rounded-2xl border border-border hover:bg-secondary flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goNext}
                  disabled={!currentFile}
                  className="flex-1 h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {stepIndex === CAPTURE_STEPS.length - 1 ? 'Review' : 'Next'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* -------- REVIEW -------- */}
          {phase === 'review' && (
            <motion.div
              key="review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={transition}
              className="absolute inset-0 flex flex-col px-6 py-6"
            >
              <div className="flex-1">
                <h2 className="text-2xl mb-2">Looks good?</h2>
                <p className="text-muted-foreground mb-6">
                  Make sure the images are sharp and readable before submitting.
                </p>

                <div className="space-y-3">
                  {CAPTURE_STEPS.map((s, i) => (
                    <div
                      key={s.key}
                      className="bg-card border border-border rounded-2xl p-3 flex gap-3 items-center"
                    >
                      <img
                        src={previews[s.key]!}
                        alt=""
                        className="w-20 h-14 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{s.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {files[s.key]?.name}
                        </div>
                      </div>
                      <button
                        onClick={() => { setStepIndex(i); setPhase('capture'); }}
                        className="h-9 px-3 rounded-lg text-sm text-[#1A6B4F] hover:bg-secondary flex items-center gap-1"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Retake
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-secondary/50 rounded-xl p-4 text-xs text-muted-foreground flex gap-2">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-[#1A6B4F]" />
                  <span>
                    Documents are reviewed by AI within seconds. If anything is
                    unclear we'll route to our team for manual review.
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => { setStepIndex(CAPTURE_STEPS.length - 1); setPhase('capture'); }}
                  className="h-14 px-6 rounded-2xl border border-border hover:bg-secondary flex items-center justify-center"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 h-14 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  Submit for verification
                </button>
              </div>
            </motion.div>
          )}

          {/* -------- SUBMITTING -------- */}
          {phase === 'submitting' && (
            <motion.div
              key="submitting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
            >
              <Loader2 className="w-12 h-12 text-[#1A6B4F] animate-spin mb-6" />
              <h2 className="text-xl mb-2">Reviewing your documents</h2>
              <p className="text-sm text-muted-foreground max-w-sm">
                This usually takes 5–15 seconds. Please keep the app open.
              </p>
            </motion.div>
          )}

          {/* -------- DONE -------- */}
          {phase === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#D4F04A] flex items-center justify-center mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#1A6B4F]" />
              </div>
              <h2 className="text-2xl mb-2">You're verified</h2>
              <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
            </motion.div>
          )}

          {/* -------- PENDING -------- */}
          {phase === 'pending' && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-6">
                <Clock className="w-10 h-10 text-amber-700" />
              </div>
              <h2 className="text-2xl mb-2">Sent for review</h2>
              <p className="text-sm text-muted-foreground max-w-sm mb-8">{message}</p>
              <button
                onClick={onSkip}
                className="h-12 px-8 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white"
              >
                Continue
              </button>
            </motion.div>
          )}

          {/* -------- ERROR -------- */}
          {phase === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <h2 className="text-2xl mb-2">Something went wrong</h2>
              <p className="text-sm text-muted-foreground max-w-sm mb-8">{message}</p>
              <button
                onClick={() => setPhase('review')}
                className="h-12 px-8 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white"
              >
                Try again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {cameraOpen && step && (
        <CameraCapture
          shape={step.shape}
          facingMode={step.facing}
          onCapture={onCameraCapture}
          onCancel={() => setCameraOpen(false)}
        />
      )}
    </div>
  );
}

function StepHint({ num, label }: { num: number; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-[#1A6B4F] text-white text-sm flex items-center justify-center shrink-0">
        {num}
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
}
